import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import ActionButton from "./ActionButton";
import { formatDateTime } from "../utils/datetime";

/**
 * Mailbox delegation across the WHOLE tenant — the audit view.
 *
 * WHY THIS EXISTS ALONGSIDE "Mailbox permissions"
 * That section edits ONE mailbox: pick it from a dropdown, see and change who
 * has Full Access or Send As. It is the remediation tool and it is the right
 * shape for that job.
 *
 * It cannot answer the question an auditor actually asks: "across every
 * mailbox, who can read or send as somebody else's mail?" Answering that with
 * a per-mailbox picker means working through the dropdown one entry at a time
 * and remembering what you saw. So this panel reads the tenant-wide scan, and
 * the two are complementary: find the exposure here, fix it there.
 *
 * THREE STATES, KEPT APART
 * never scanned / scanned-but-partial / scanned-and-clean are three different
 * statements about a customer's mail security. Rendering "no delegation found"
 * when nobody has looked, or when only part of a large tenant was covered,
 * would be a false all-clear — so each has its own wording.
 *
 * The scan is minutes of Exchange PowerShell (~3.4s per mailbox, measured), so
 * the POST only STARTS it; this polls for the result.
 */

type Scan = {
  scanned_at: string;
  total_mailboxes: number;
  examined: number;
  stopped_early: boolean;
  error: string | null;
};

type Grant = {
  mailbox: string;
  mailbox_name: string | null;
  mailbox_kind: string | null;
  grantee: string;
  kind: "full_access" | "send_as" | "send_on_behalf" | string;
};

const CARD =
  "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const H = "text-sm font-semibold text-gray-800 dark:text-gray-100";
const SUB = "text-xs text-gray-500 dark:text-gray-400";

// Send AS first: it is impersonation with no marker a recipient can see, and
// it is the grant people forget exists. Then read access. Then send-on-behalf,
// which IS visible to the recipient and so is the mildest of the three.
const KINDS: { key: Grant["kind"]; label: string; blurb: string; tone: string }[] = [
  {
    key: "send_as",
    label: "Send AS",
    blurb: "sends mail that IS FROM the mailbox — a recipient cannot tell",
    tone: "text-red-700 dark:text-red-400",
  },
  {
    key: "full_access",
    label: "Full access",
    blurb: "can open the mailbox and read everything in it",
    tone: "text-amber-700 dark:text-amber-400",
  },
  {
    key: "send_on_behalf",
    label: "Send on behalf of",
    blurb: "visible to the recipient",
    tone: "text-blue-700 dark:text-blue-400",
  },
];

export default function M365DelegationAudit({
  customerId,
  enabled,
}: {
  customerId: number | null;
  /** Exchange Online PowerShell provisioned for this tenant. */
  enabled: boolean;
}) {
  const [scan, setScan] = useState<Scan | null>(null);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const pollRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (customerId == null) return null;
    try {
      const r = await api.get(`/api/v1/m365/${customerId}/mailbox-delegation`);
      setScan(r.data.scan || null);
      setGrants(r.data.grants || []);
      setError("");
      return (r.data.scan || null) as Scan | null;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Could not read the delegation scan");
      return null;
    } finally {
      setLoaded(true);
    }
  }, [customerId]);

  useEffect(() => {
    setLoaded(false);
    setScan(null);
    setGrants([]);
    load();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [load]);

  const startScan = async () => {
    if (customerId == null) return;
    const before = scan?.scanned_at ?? null;
    await api.post(`/api/v1/m365/${customerId}/mailbox-delegation/scan`);
    setRunning(true);
    // The POST only starts it. Poll for a NEW scanned_at rather than a fixed
    // delay: a small tenant finishes in ~2 minutes and a large one runs to its
    // time budget, so any single wait would be wrong for one of them.
    if (pollRef.current) window.clearInterval(pollRef.current);
    let waited = 0;
    pollRef.current = window.setInterval(async () => {
      waited += 5;
      const s = await load();
      if ((s && s.scanned_at !== before) || waited > 15 * 60) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        pollRef.current = null;
        setRunning(false);
      }
    }, 5000);
  };

  if (customerId == null) {
    return (
      <div className={`${CARD} p-4`}>
        <h3 className={H}>Mailbox delegation across all mailboxes</h3>
        <p className={`${SUB} mt-1`}>
          Pick a customer in the header to audit their mailbox delegation.
        </p>
      </div>
    );
  }

  const byKind = (k: string) => grants.filter((g) => g.kind === k);

  return (
    <div className={`${CARD} p-4`}>
      <div className="flex items-start gap-3">
        <div>
          <h3 className={H}>Mailbox delegation across all mailboxes</h3>
          <p className={`${SUB} mt-0.5`}>
            Who can open, or send as, somebody else&apos;s mailbox — the whole tenant in
            one view. Use <span className="font-medium">Mailbox permissions</span> below to
            change any of it.
          </p>
        </div>
        {enabled && (
          <ActionButton
            onAction={startScan}
            variant="secondary"
            size="sm"
            className="ml-auto shrink-0"
            pendingLabel="Starting…"
          >
            {scan ? "Re-scan" : "Scan now"}
          </ActionButton>
        )}
      </div>

      {!enabled ? (
        <p className={`${SUB} italic mt-3`}>
          Exchange Online management is not provisioned for this tenant, so delegation
          cannot be read. Re-run the Microsoft 365 setup for this customer.
        </p>
      ) : (
        <>
          {error && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          {running && (
            <p className={`${SUB} mt-3`}>
              Scanning… this takes a couple of minutes on a small tenant (Exchange is
              queried once per mailbox). You can leave this page.
            </p>
          )}

          {/* THE THREE STATES. Never collapse these: a customer told "no
              delegation found" when nobody looked has been given a false
              all-clear. */}
          {loaded && !scan && !running && (
            <p className={`${SUB} mt-3`}>
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                Never scanned.
              </span>{" "}
              Nothing here yet — which is not the same as no delegation existing.
            </p>
          )}

          {scan && (
            <div className="mt-3 text-xs">
              <span className="text-gray-500 dark:text-gray-400">
                Last scanned {formatDateTime(scan.scanned_at)} ·{" "}
              </span>
              {scan.stopped_early ? (
                <span className="text-amber-700 dark:text-amber-400 font-semibold">
                  PARTIAL — {scan.examined} of {scan.total_mailboxes} mailboxes examined
                  before the time budget ran out. Delegation may exist on the rest.
                </span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">
                  {scan.examined} mailboxes examined
                  {scan.examined < scan.total_mailboxes && (
                    <> (of {scan.total_mailboxes}; the rest are Exchange system mailboxes)</>
                  )}
                </span>
              )}
              {scan.error && (
                <div className="mt-1 text-red-600 dark:text-red-400">
                  Last scan reported: {scan.error}
                </div>
              )}
            </div>
          )}

          {scan && grants.length === 0 && !scan.error && (
            <p className={`${SUB} mt-3`}>
              No delegation found on the mailboxes examined.
            </p>
          )}

          {grants.length > 0 && (
            <div className="mt-4 space-y-4">
              {KINDS.map(({ key, label, blurb, tone }) => {
                const rows = byKind(key);
                if (rows.length === 0) return null;
                // Group by mailbox: a mailbox five people can open is one
                // finding with five names, not five findings.
                const grouped = new Map<string, Grant[]>();
                rows.forEach((g) => {
                  const list = grouped.get(g.mailbox) || [];
                  list.push(g);
                  grouped.set(g.mailbox, list);
                });
                const ordered = [...grouped.entries()].sort(
                  (a, b) => b[1].length - a[1].length,
                );
                return (
                  <div key={key}>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-sm font-semibold ${tone}`}>{label}</span>
                      <span className={SUB}>{blurb}</span>
                    </div>
                    <div className="mt-1 divide-y divide-gray-100 dark:divide-gray-700">
                      {ordered.map(([mailbox, entries]) => (
                        <div key={mailbox} className="py-2 text-sm">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {mailbox}
                            </span>
                            {/* A SHARED mailbox with delegates is normal; a
                                USER mailbox with delegates is the finding. */}
                            {entries[0].mailbox_kind && (
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                  entries[0].mailbox_kind === "UserMailbox"
                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                }`}
                              >
                                {entries[0].mailbox_kind.replace("Mailbox", " mailbox")}
                              </span>
                            )}
                            <span className={SUB}>
                              {entries.length} {entries.length === 1 ? "person" : "people"}
                            </span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-400 mt-0.5">
                            {entries.map((e) => e.grantee).sort().join(", ")}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
